import React, {useCallback, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AdminService} from '@/modules/admin/services/adminService';
import Card from '@/shared/components/Card';
import {logError} from '@/shared/utils/logger';

interface Props {
    className?: string;
}

interface RejectedEmail {
    created: number;
    type: string;
    email: string;
    reason: string;
}

/**
 * RejectedEmails component - Admin page to view and manage rejected emails
 * Refactored to follow docs/SUMMARY.md best practices
 */
export const RejectedEmails: React.FC<Props> = () => {
    const [rejectedEmails, setRejectedEmails] = useState<RejectedEmail[]>([]);
    const [dateFilter, setDateFilter] = useState({
        startDatetime: new Date(),
        endDatetime: new Date(),
    });
    const [hasSearched, setHasSearched] = useState(false);

    // Mutation for searching rejected emails
    const searchMutation = useMutation({
        mutationFn: ({startDatetime, endDatetime}: {startDatetime: Date; endDatetime: Date}) => {
            const startTimestamp = Math.floor(startDatetime.getTime() / 1000);
            const endTimestamp = Math.ceil(endDatetime.getTime() / 1000);
            return AdminService.getRejectedEmails(startTimestamp, endTimestamp);
        },
        onSuccess: (data) => {
            setRejectedEmails(data);
            setHasSearched(true);
        },
        onError: (error) => {
            logError('Failed to search rejected emails', error);
            setHasSearched(true);
        },
    });

    const handleSearch = useCallback(() => {
        if (!dateFilter.startDatetime || !dateFilter.endDatetime) {
            return;
        }
        searchMutation.mutate(dateFilter);
    }, [dateFilter, searchMutation]);

    const formatDate = useCallback((timestamp: number) => {
        if (!timestamp) return '';
        return new Date(timestamp * 1000).toLocaleString();
    }, []);

    return (
        <div className="space-y-6">
            <Card>
                <Card.Header>
                    <Card.Title>Search for rejected emails in database</Card.Title>
                </Card.Header>
                <Card.Content>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-mld-text-secondary mb-1">Start date/time</label>
                                <input
                                    type="datetime-local"
                                    value={dateFilter.startDatetime.toISOString().slice(0, 16)}
                                    onChange={(e) =>
                                        setDateFilter((prev) => ({
                                            ...prev,
                                            startDatetime: new Date(e.target.value),
                                        }))
                                    }
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-mld-text-secondary mb-1">End date/time</label>
                                <input
                                    type="datetime-local"
                                    value={dateFilter.endDatetime.toISOString().slice(0, 16)}
                                    onChange={(e) =>
                                        setDateFilter((prev) => ({
                                            ...prev,
                                            endDatetime: new Date(e.target.value),
                                        }))
                                    }
                                    className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={searchMutation.isPending || !dateFilter.startDatetime || !dateFilter.endDatetime}
                            className="px-4 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90 focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            {searchMutation.isPending ? 'Searching...' : 'Search Rejected Emails'}
                        </button>
                    </div>
                </Card.Content>
            </Card>

            {hasSearched && (
                <Card>
                    <Card.Header>
                        <Card.Title>Rejected Emails ({rejectedEmails.length})</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        {rejectedEmails.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-mld-muted-text">No rejected emails found for the selected date range.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-mld-panel-border">
                                    <thead className="bg-mld-menu-background">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                                Type
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-mld-muted-text uppercase tracking-wider">
                                                Reason
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-mld-panel-border">
                                        {rejectedEmails.map((email: RejectedEmail, index) => (
                                            <tr key={index} className="hover:bg-mld-menu-background">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                    {formatDate(email.created)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                    {email.type}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-mld-text-primary">
                                                    {email.email}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-mld-text-primary">
                                                    <div className="max-w-xs truncate" title={email.reason}>
                                                        {email.reason}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card.Content>
                </Card>
            )}
        </div>
    );
};

export default RejectedEmails;
